import Flex from "../../components/Flex";
import { Box, Button, Divider, FormControlLabel, ListItem, Radio, RadioGroup, Typography } from "@mui/material";
import { nonEmpty } from "../../helpers/coll";
import BulkEditDialog from "./BulkEditDialog";
import { ChangeEvent, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import {
  clearSelectedRules,
  fetchLensAndRules,
  selectDraftMode,
  selectLensEditorLens,
  selectSelectedRules,
} from "../../redux/slices/lensEditor";
import { backend, LensPatchApplyDuplicateMode } from "../../Backend";
import triggerDownload from "../../helpers/triggerDownload";
import { readFileToString } from "../../helpers/readFileToString";
import { P } from "theme/typography";
import BulkDeleteDialog from "./BulkDeleteDialog";
import { LoadingButton } from "@mui/lab";
import { RuleMode } from "routes/lens-editor/RuleMode";

const SelectedRules = () => {
  const selectedRules = useAppSelector(selectSelectedRules);
  const draftMode = useAppSelector(selectDraftMode);
  const lens = useAppSelector(selectLensEditorLens);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [patchFiles, setPatchFiles] = useState<Record<string, object>[]>([]);
  const [applyingPatches, setApplyingPatches] = useState(false);

  const [duplicateMode, setDuplicateMode] = useState<LensPatchApplyDuplicateMode>("IgnoreByQuery");

  const handleDuplicateModeChange = (event: ChangeEvent<HTMLInputElement>) =>
    setDuplicateMode(event.target.value as LensPatchApplyDuplicateMode);

  const dispatch = useAppDispatch();

  const openEditDialog = () => setEditDialogOpen(true);
  const openDeleteDialog = () => setDeleteDialogOpen(true);

  const handleDeselect = () => {
    dispatch(clearSelectedRules());
  };

  const makeSelectedRulePatch = async () => {
    const ruleIds = selectedRules.map((r) => r.ruleId);
    const { patch, filename } = await backend.makeLensPatch({ lensId: lens.id, addRuleIds: ruleIds });
    triggerDownload(new Blob([patch]), filename);
  };

  const applyLensPatches = async () => {
    setApplyingPatches(true);
    for (const patchFile of patchFiles) {
      await backend.applyLensPatch(lens.id, duplicateMode, JSON.stringify(patchFile));
    }
    await dispatch(fetchLensAndRules({ lensId: lens.id }));
    setApplyingPatches(false);
    setPatchFiles([]);
  };

  const onFile = async (evt: ChangeEvent<HTMLInputElement>) => {
    evt.persist();
    const files = evt.target.files;

    if (!files) {
      return;
    }

    const promises = Array.from(files).map((file) => readFileToString(file));
    const results = await Promise.all(promises);
    const patches = results.map((result) => JSON.parse(result));
    setPatchFiles(patches);
  };

  return (
    <Box>
      <Flex>
        <p className="text-lg font-bold p-2">Patch</p>
        <Button size="small" component="label" disabled={!draftMode}>
          Choose patch files
          <input
            multiple
            type="file"
            autoComplete="off"
            tabIndex={-1}
            accept="application/json"
            style={{ display: "none" }}
            onChange={onFile}
          />
        </Button>
        <Box>
          {patchFiles.map((patchFile, idx) => {
            return (
              <Flex key={idx}>
                {Object.entries(patchFile).map(([key, value]) => {
                  const v = value as any;
                  return (
                    <P key={key}>
                      {key}: {key === "entries" ? v?.length : v?.toString()}
                    </P>
                  );
                })}
              </Flex>
            );
          })}
        </Box>
        <LoadingButton
          size="small"
          onClick={applyLensPatches}
          disabled={patchFiles.length === 0 || !draftMode}
          loading={applyingPatches}>
          Apply patch
        </LoadingButton>
        <RadioGroup
          name="allow-duplicates-radio-buttons-group"
          value={duplicateMode}
          sx={{ pl: 1 }}
          onChange={handleDuplicateModeChange}>
          <FormControlLabel value="IgnoreByQuery" label="Ignore Duplicates by Concept ID" control={<Radio />} />
          <FormControlLabel
            value="IgnoreByQueryAndLocation"
            label="Ignore Duplicates by Description and Subtype"
            control={<Radio />}
          />
          <FormControlLabel value="Allow" label="Allow Duplicates" control={<Radio />} />
        </RadioGroup>
      </Flex>
      <Divider />

      <Flex flexDirection="row">
        <Typography variant="h6">Selections: {selectedRules.length}</Typography>
        {nonEmpty(selectedRules) && (
          <>
            <Button size="small" onClick={handleDeselect}>
              Deselect All
            </Button>
            <Button size="small" onClick={openEditDialog}>
              Edit All
            </Button>
            <Button size="small" onClick={openDeleteDialog}>
              Delete All
            </Button>
            <Button size="small" onClick={makeSelectedRulePatch}>
              Save Selection as Patch
            </Button>
          </>
        )}
      </Flex>
      <Box
        sx={{
          overflowY: "auto",
        }}>
        {selectedRules.map((rule) => (
          <ListItem key={rule.ruleId} divider>
            <RuleMode rule={rule} />
          </ListItem>
        ))}
      </Box>
      <BulkDeleteDialog open={deleteDialogOpen} close={() => setDeleteDialogOpen(false)} />
      <BulkEditDialog open={editDialogOpen} close={() => setEditDialogOpen(false)} />
    </Box>
  );
};

export default SelectedRules;
